import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Server, Activity, Database, Clock, User, RefreshCw, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface SystemLog {
  id: number;
  action: string;
  resource: string;
  details: string;
  userName: string;
  createdAt: string;
}

interface IntegrationRecord {
  type: 'CUSTOMER' | 'ORDER';
  id: string;
  name: string;
  syncStatus: 'PENDING' | 'SYNCHRONIZED' | 'FAILED';
  syncErrors: string | null;
  blingId: string | null;
  updatedAt: string;
}

export const LogsView = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [activeTab, setActiveTab] = useState<'logs' | 'integrations'>('logs');
  
  // Logs State
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [selectedLog, setSelectedLog] = useState<SystemLog | null>(null);
  const [userFilter, setUserFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');

  // Integrations State
  const [integrations, setIntegrations] = useState<IntegrationRecord[]>([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  const [selectedIntegrationError, setSelectedIntegrationError] = useState<IntegrationRecord | null>(null);
  const [isRetrying, setIsRetrying] = useState<string | null>(null);

  const API_URL = import.meta.env.VITE_API_URL || 'https://techtennis-api.vercel.app';
  const getAuthHeader = () => {
    const token = localStorage.getItem('tt_auth_token');
    return { 'Authorization': `Bearer ${token}` };
  };

  const BFF_URL = import.meta.env.VITE_BFF_URL || 'https://techtennis-bff.vercel.app';

  const fetchLogs = async (page: number = 1) => {
    setIsLoadingLogs(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: '50',
      });
      if (userFilter) queryParams.append('user', userFilter);
      if (resourceFilter) queryParams.append('resource', resourceFilter);

      const res = await fetch(`${API_URL}/api/logs?${queryParams.toString()}`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setLogs(data);
          setTotalPages(1);
        } else {
          setLogs(data.logs || []);
          setTotalPages(data.totalPages || 1);
          setTotalLogs(data.total || 0);
          setCurrentPage(data.page || 1);
        }
      }
    } catch(err) {
      console.error(err);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const fetchIntegrations = async () => {
    setIsLoadingIntegrations(true);
    try {
      const res = await fetch(`${BFF_URL}/api/integrations`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setIntegrations(data);
      }
    } catch(err) {
      console.error(err);
    } finally {
      setIsLoadingIntegrations(false);
    }
  };

  const retryIntegration = async (type: string, id: string) => {
    setIsRetrying(id);
    try {
      const res = await fetch(`${BFF_URL}/api/integrations/${type}/${id}/retry`, { 
        method: 'POST',
        headers: getAuthHeader()
      });
      if (res.ok) {
        // Optimistically update
        setIntegrations(prev => prev.map(i => i.id === id ? { ...i, syncStatus: 'PENDING', syncErrors: null } : i));
        // Refetch to get latest
        await fetchIntegrations();
      } else {
        alert('Falha ao reestimular integração');
      }
    } catch(err) {
      console.error(err);
      alert('Erro de conexão ao reestimular.');
    } finally {
      setIsRetrying(null);
    }
  };

  useEffect(() => {
    if (currentUser?.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    if (activeTab === 'logs') {
      fetchLogs(currentPage);
    } else {
      fetchIntegrations();
    }
  }, [currentUser, currentPage, activeTab]);

  const getActionColor = (action: string) => {
    if (action === 'CREATE') return '#4ADE80';
    if (action === 'UPDATE') return '#FBBF24';
    if (action === 'DELETE') return '#F87171';
    return '#9CA3AF';
  };

  const getSyncStatusBadge = (status: string, hasError: boolean) => {
    if (hasError) {
      return <span style={{ background: 'rgba(248, 113, 113, 0.2)', color: '#F87171', padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}><AlertCircle size={14}/> ERRO</span>;
    }
    if (status === 'SYNCHRONIZED') {
      return <span style={{ background: 'rgba(74, 222, 128, 0.2)', color: '#4ADE80', padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}><CheckCircle size={14}/> SINCRONIZADO</span>;
    }
    return <span style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#FBBF24', padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}><Clock size={14}/> PENDENTE</span>;
  };

  const renderFriendlyDetails = (details: string) => {
    if (!details) return '-';
    try {
      const parsed = JSON.parse(details);
      const keys = Object.keys(parsed);
      if (keys.length === 0) return 'Sem alterações';
      
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {keys.slice(0, 3).map(k => (
             <div key={k} style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
               <strong style={{ color: 'var(--text-primary)' }}>{k}:</strong> {String(parsed[k])}
             </div>
          ))}
          {keys.length > 3 && <div style={{ fontSize: '11px', color: 'var(--primary-color)' }}>+ {keys.length - 3} campos alterados</div>}
        </div>
      );
    } catch (e) {
      return (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '12px' }}>
          {details}
        </div>
      );
    }
  };

  return (
    <div style={{ paddingTop: '140px', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: '1200px', padding: '0 24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={() => navigate('/')} style={{ background: 'var(--bg-panel)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <ArrowLeft size={24} />
            </button>
            <h1 style={{ fontSize: '32px', fontWeight: 800, margin: 0, fontFamily: 'var(--font-heading)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Server size={32} color="var(--primary-color)" /> Logs do Sistema
            </h1>
          </div>
          
          <div style={{ display: 'flex', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '4px' }}>
            <button 
              onClick={() => setActiveTab('logs')}
              style={{ 
                padding: '10px 20px', 
                borderRadius: '6px', 
                border: 'none', 
                background: activeTab === 'logs' ? 'var(--primary-color)' : 'transparent', 
                color: activeTab === 'logs' ? 'white' : 'white', 
                fontWeight: 600, 
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Logs de Atividade
            </button>
            <button 
              onClick={() => setActiveTab('integrations')}
              style={{ 
                padding: '10px 20px', 
                borderRadius: '6px', 
                border: 'none', 
                background: activeTab === 'integrations' ? 'var(--primary-color)' : 'transparent', 
                color: activeTab === 'integrations' ? 'white' : 'white', 
                fontWeight: 600, 
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Integrações Bling
            </button>
          </div>
        </div>

        {/* LOGS TAB */}
        {activeTab === 'logs' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input 
                type="text" 
                placeholder="Usuário (ex: email)" 
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: 'white', outline: 'none' }}
                onKeyDown={(e) => e.key === 'Enter' && fetchLogs(1)}
              />
              <input 
                type="text" 
                placeholder="Recurso (ex: settings, job)" 
                value={resourceFilter}
                onChange={(e) => setResourceFilter(e.target.value)}
                style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: 'white', outline: 'none' }}
                onKeyDown={(e) => e.key === 'Enter' && fetchLogs(1)}
              />
              <button onClick={() => fetchLogs(1)} className="button-primary" style={{ padding: '10px 20px', borderRadius: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Activity size={20} /> Filtrar
              </button>
            </div>

            <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', overflowX: 'auto' }}>
              {isLoadingLogs ? (
                <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>Carregando logs...</div>
              ) : logs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>Nenhum log encontrado.</div>
              ) : (
                <table style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse', textAlign: 'left', color: 'white' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-secondary)' }}>
                      <th style={{ padding: '16px', fontWeight: 600 }}>Ação</th>
                      <th style={{ padding: '16px', fontWeight: 600 }}>Recurso</th>
                      <th style={{ padding: '16px', fontWeight: 600 }}>Usuário</th>
                      <th style={{ padding: '16px', fontWeight: 600 }}>Data/Hora</th>
                      <th style={{ padding: '16px', fontWeight: 600 }}>Detalhes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <motion.tr 
                        key={log.id} 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }}
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                      >
                        <td style={{ padding: '16px' }}>
                          <span style={{ 
                            background: `${getActionColor(log.action)}20`, 
                            color: getActionColor(log.action), 
                            padding: '4px 12px', 
                            borderRadius: '100px', 
                            fontSize: '12px', 
                            fontWeight: 700 
                          }}>
                            {log.action}
                          </span>
                        </td>
                        <td style={{ padding: '16px', fontWeight: 600 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Database size={16} color="var(--text-secondary)" /> {log.resource}
                          </div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <User size={16} color="var(--text-secondary)" /> {log.userName}
                          </div>
                        </td>
                        <td style={{ padding: '16px', color: 'var(--text-secondary)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Clock size={16} /> {new Date(log.createdAt).toLocaleString('pt-BR')}
                          </div>
                        </td>
                        <td style={{ padding: '16px', maxWidth: '300px' }}>
                          <div 
                            onClick={() => setSelectedLog(log)}
                            style={{ 
                            background: 'rgba(0,0,0,0.2)', 
                            padding: '12px', 
                            borderRadius: '8px', 
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                            border: '1px solid rgba(255,255,255,0.05)',
                            transition: 'all 0.2s ease'
                          }} 
                            onMouseEnter={(e) => e.currentTarget.style.border = '1px solid var(--primary-color)'}
                            onMouseLeave={(e) => e.currentTarget.style.border = '1px solid rgba(255,255,255,0.05)'}
                          >
                            {renderFriendlyDetails(log.details)}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: 'var(--bg-panel)', borderRadius: '12px' }}>
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                  Mostrando página {currentPage} de {totalPages} ({totalLogs} logs no total)
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                    disabled={currentPage === 1}
                    style={{ background: currentPage === 1 ? 'rgba(255,255,255,0.05)' : 'var(--primary-color)', color: currentPage === 1 ? 'var(--text-secondary)' : 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                  >
                    Anterior
                  </button>
                  <button 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                    disabled={currentPage === totalPages}
                    style={{ background: currentPage === totalPages ? 'rgba(255,255,255,0.05)' : 'var(--primary-color)', color: currentPage === totalPages ? 'var(--text-secondary)' : 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* INTEGRATIONS TAB */}
        {activeTab === 'integrations' && (
          <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', overflowX: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white' }}>Status das Integrações com Bling</h2>
              <button onClick={() => fetchIntegrations()} className="button-secondary" style={{ padding: '8px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <RefreshCw size={16} /> Atualizar
              </button>
            </div>

            {isLoadingIntegrations ? (
              <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>Carregando integrações...</div>
            ) : integrations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>Nenhum registro de integração.</div>
            ) : (
              <table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse', textAlign: 'left', color: 'white' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-secondary)' }}>
                    <th style={{ padding: '16px', fontWeight: 600 }}>Tipo</th>
                    <th style={{ padding: '16px', fontWeight: 600 }}>Identificador</th>
                    <th style={{ padding: '16px', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '16px', fontWeight: 600 }}>Bling ID</th>
                    <th style={{ padding: '16px', fontWeight: 600 }}>Última Tentativa</th>
                    <th style={{ padding: '16px', fontWeight: 600, textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {integrations.map(integration => {
                    const hasError = !!integration.syncErrors;
                    return (
                      <motion.tr 
                        key={`${integration.type}-${integration.id}`} 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }}
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: hasError ? 'rgba(248, 113, 113, 0.05)' : 'transparent' }}
                      >
                        <td style={{ padding: '16px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                          {integration.type === 'CUSTOMER' ? 'Cliente' : 'Pedido'}
                        </td>
                        <td style={{ padding: '16px' }}>
                          <span style={{ fontWeight: 600, color: 'white' }}>{integration.name}</span>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>ID: {integration.id}</div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          {getSyncStatusBadge(integration.syncStatus, hasError)}
                        </td>
                        <td style={{ padding: '16px', fontFamily: 'monospace', color: integration.blingId ? '#A78BFA' : 'var(--text-secondary)' }}>
                          {integration.blingId || '-'}
                        </td>
                        <td style={{ padding: '16px', color: 'var(--text-secondary)' }}>
                          {new Date(integration.updatedAt).toLocaleString('pt-BR')}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            {hasError && (
                              <>
                                <button 
                                  onClick={() => setSelectedIntegrationError(integration)}
                                  style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                                >
                                  <Info size={14}/> Motivo
                                </button>
                                <button 
                                  onClick={() => retryIntegration(integration.type, integration.id)}
                                  disabled={isRetrying === integration.id}
                                  style={{ background: '#3B82F6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: isRetrying === integration.id ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: isRetrying === integration.id ? 0.7 : 1 }}
                                >
                                  <RefreshCw size={14} className={isRetrying === integration.id ? 'spin' : ''} /> 
                                  Reestimular
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

      </div>

      {/* Log Details Modal (Activity Logs) */}
      {selectedLog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ background: 'var(--bg-panel-solid)', width: '100%', maxWidth: '600px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ padding: '24px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '20px', color: 'white' }}>Detalhes do Log</h3>
              <button onClick={() => setSelectedLog(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '24px' }}>&times;</button>
            </div>
            <div style={{ padding: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div><strong style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Ação</strong><span style={{ color: getActionColor(selectedLog.action), fontWeight: 700 }}>{selectedLog.action}</span></div>
                <div><strong style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Recurso</strong><span style={{ color: 'white' }}>{selectedLog.resource}</span></div>
                <div><strong style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Usuário</strong><span style={{ color: 'white' }}>{selectedLog.userName}</span></div>
                <div><strong style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Data/Hora</strong><span style={{ color: 'white' }}>{new Date(selectedLog.createdAt).toLocaleString('pt-BR')}</span></div>
              </div>
              <strong style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '12px' }}>Dados Alterados / Carga Completa:</strong>
              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '16px', borderRadius: '8px', overflowX: 'auto' }}>
                {(() => {
                  try {
                    const parsed = JSON.parse(selectedLog.details);
                    return <pre style={{ margin: 0, color: '#A78BFA', fontSize: '14px', fontFamily: 'monospace' }}>{JSON.stringify(parsed, null, 2)}</pre>;
                  } catch (e) {
                    return <pre style={{ margin: 0, color: '#A78BFA', fontSize: '14px', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{selectedLog.details}</pre>;
                  }
                })()}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Integration Error Modal */}
      {selectedIntegrationError && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ background: 'var(--bg-panel-solid)', width: '100%', maxWidth: '600px', borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
            <div style={{ padding: '24px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(248, 113, 113, 0.1)' }}>
              <h3 style={{ margin: 0, fontSize: '20px', color: '#F87171', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertCircle size={24} /> Erro de Integração
              </h3>
              <button onClick={() => setSelectedIntegrationError(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '24px' }}>&times;</button>
            </div>
            <div style={{ padding: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '24px' }}>
                <div><strong style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Registro</strong><span style={{ color: 'white' }}>{selectedIntegrationError.name} ({selectedIntegrationError.type})</span></div>
              </div>
              <strong style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '12px' }}>Motivo Exato do Erro:</strong>
              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '16px', borderRadius: '8px', overflowX: 'auto', borderLeft: '4px solid #F87171' }}>
                {(() => {
                  try {
                    const parsed = JSON.parse(selectedIntegrationError.syncErrors || '{}');
                    return <pre style={{ margin: 0, color: '#FCA5A5', fontSize: '14px', fontFamily: 'monospace' }}>{JSON.stringify(parsed, null, 2)}</pre>;
                  } catch (e) {
                    return <pre style={{ margin: 0, color: '#FCA5A5', fontSize: '14px', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{selectedIntegrationError.syncErrors}</pre>;
                  }
                })()}
              </div>
              
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button onClick={() => setSelectedIntegrationError(null)} className="button-secondary" style={{ padding: '10px 20px', borderRadius: '8px' }}>
                  Fechar
                </button>
                <button 
                  onClick={() => {
                    retryIntegration(selectedIntegrationError.type, selectedIntegrationError.id);
                    setSelectedIntegrationError(null);
                  }}
                  className="button-primary" 
                  style={{ padding: '10px 20px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <RefreshCw size={18} /> Reestimular Agora
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
